// Frontend mirror of supabase/functions/asp-trips/vas-relay.ts.
//
// Both the EAPI callback path (asp-trips) and the driver app path
// (useDriverLegStatusUpdate) POST /v1/vas/stage-transition, so both must make
// the SAME relay decision. Keep these two files in lockstep.

const NUMERIC_RE = /^\d+$/;
/**
 * Partner-supplied per-leg load ids arrive as `{numericLoadId}-{legOrdinal}`
 * because each leg lands as its own order. The VAS trip id is the numeric
 * base; the ordinal is the leg discriminator.
 */
const SUFFIXED_RE = /^(\d+)-(\d+)$/;

export interface NormalizedTender {
  /** Numeric VAS trip id, or null when the tender is not VAS-shaped. */
  vasTripId: string | null;
  /** Leg ordinal parsed out of a suffixed tender, if any. */
  legOrdinal: string | null;
}

export function normalizeTenderId(tenderId: string | null | undefined): NormalizedTender {
  const raw = (tenderId ?? "").trim();
  if (!raw) return { vasTripId: null, legOrdinal: null };
  if (NUMERIC_RE.test(raw)) return { vasTripId: raw, legOrdinal: null };
  const m = raw.match(SUFFIXED_RE);
  if (m) return { vasTripId: m[1], legOrdinal: m[2] };
  return { vasTripId: null, legOrdinal: null };
}

export type RelayDecision =
  | { relay: true; payload: Record<string, unknown> }
  | { relay: false; reason: string };

export function buildRelayDecision(args: {
  tenderId: string | null;
  orgId: string | null;
  currentStage: string;
  /** `legs.customer_leg_id` when the transition resolved through a leg. */
  legCustomerLegId: string | null;
  /**
   * `customer_tenant_org_ids.vas_enabled`. An org mapping alone is NOT a VAS
   * signal — that table is also the Onramp org mapping used for TaaS inbound
   * tenant resolution, so most non-VAS customers have a row.
   */
  vasEnabled: boolean;
}): RelayDecision {
  if (!args.orgId) {
    return {
      relay: false,
      reason: "not_vas_customer: no customer_tenant_org_ids mapping for this tenant/customer",
    };
  }
  if (!args.vasEnabled) {
    return {
      relay: false,
      reason: "not_vas_customer: customer is not vas_enabled for this tenant",
    };
  }
  const { vasTripId, legOrdinal } = normalizeTenderId(args.tenderId);
  if (!vasTripId) {
    return {
      relay: false,
      reason: args.tenderId
        ? `not_vas_trip: tender_id ${JSON.stringify(args.tenderId)} is not a numeric VAS trip id`
        : "not_vas_trip: order has no tender_id (customer_load_id)",
    };
  }
  // The leg's own partner-supplied id always wins; the tender suffix is only
  // a fallback discriminator for per-leg orders with no customer_leg_id.
  const customerLegId = args.legCustomerLegId ?? legOrdinal;
  return {
    relay: true,
    payload: {
      vas_trip_id: vasTripId,
      current_stage: args.currentStage,
      org_id: args.orgId,
      ...(customerLegId ? { customer_leg_id: customerLegId } : {}),
    },
  };
}
